import differenceWith from 'lodash/differenceWith'
import isEqual from 'lodash/isEqual'
import { useGtm } from '@gtm-support/vue-gtm'

function eventItemName(event: CmsData) {
  const date = new Date(event.eventDate)
  const datePart = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`
  const timePart = `${('0' + date.getHours()).slice(-2)}:${('0' + date.getMinutes()).slice(-2)}:${('0' + date.getSeconds()).slice(-2)}`
  return `${event.production?.title || 'Unknown'}-${datePart} ${timePart}`
}

/**
 * An event whenever a page is visited by clicking in the navigation tree
 */
export function setupGtmNavigationClick() {
  if (import.meta.client) {
    const gtm = useGtm()
    return (label?: string) =>
      gtm?.push({
        event: 'navigation_click',
        events: {
          category: 'navigation',
          action: 'navigation_click',
          label,
        },
      })
  }
  return () => {}
}

/**
 * An event whenever a page is visited
 */
export function setupGtmPageView(page: CmsData) {
  if (import.meta.client) {
    const gtm = useGtm()
    // Schedule a GTM event with custom page data
    // Note that we mimic the 250ms delay used by the gtm-module
    // https://github.com/nuxt-community/gtm-module/blob/master/lib/plugin.js#L76
    setTimeout(() => {
      gtm?.push({
        event: 'page_view',
        page: {
          category: page?.__typename,
          language_code: page?.language,
          referrer: window.__internalReferrer || document.referrer || null,
        },
      })
    }, 250)
  }
}
/**
 * An event with the user information
 */
let previousUserInfoJson: string | null = null
export function setupGtmUserInfo() {
  if (import.meta.client) {
    const gtm = useGtm()
    const tixStore = useTixStore()

    // Listen to changes to the Tix state
    watch(
      () => [tixStore.user, tixStore.customerId],
      ([user, customerId]) => {
        setTimeout(() => {
          const userInfo = !user?.id
            ? { logged_in: 'false' }
            : user?.id && customerId
              ? {
                  logged_in: 'true',
                  user_id: user?.id.toString(),
                  customer_id: customerId.toString(),
                }
              : undefined

          // Only push the user info if it has changed to prevent duplicate events
          if (JSON.stringify(userInfo) === previousUserInfoJson) {
            return
          }

          if (userInfo !== undefined) {
            gtm?.push({
              event: 'user_info',
              user: userInfo,
            })
            previousUserInfoJson = JSON.stringify(userInfo)
          }
        }, 250)
      },
    )
  }
}

/**
 * An event that shows the listed items on the page, for CGBR this is usually a list with events
 */
export function setupGtmViewItemList(listName: 'calendar' | 'NETA' | 'keuzehulp') {
  if (import.meta.client) {
    const gtm = useGtm()
    const listNameMap = { calendar: 1, NETA: 2, keuzehulp: 3 }
    return (events: CmsData[], offset: number) =>
      setTimeout(() => {
        gtm?.push({
          event: 'view_item_list',
          ecommerce: {
            items: events.map((event: CmsData, index: number) => ({
              index: offset + index,
              item_list_name: listName,
              item_list_id: listNameMap[listName],
              item_brand: event.production?.organizer?.title || 'Unknown',
              item_category: event.production?.title || 'Unknown',
              item_category_2: event.room?.title || 'Unknown',
              item_date: event.eventDate || 'Unknown',
              item_genre: event.production?.genres?.map(({ title }: { title?: string }) => title)?.join(', ') || 'Unknown',
              item_id: event.eventIdentifier || 'Unknown',
              item_name: eventItemName(event),
              price: event.minPrice,
              quantity: 1,
            })),
          },
        })
      }, 250)
  }
  return () => {}
}

/**
 * This is the setup for a watcher that sends the view_item_list event with all the events on the page
 *
 * Note that we will want to remove this in the future in favor of sending separate view_item_list events
 * every time one of the items scrolls into view.  This is currently already done for the NETA events,
 * see EventDetailPageNeta.vue
 */
export function setupGtmViewItemListWatcher(storeStateName: string) {
  const viewItemList = setupGtmViewItemList('calendar')
  // Only the `event-overview` module is used at present; the state property name is taken from
  // the part after the slash so this keeps working the way the Vuex getter lookup did.
  const eventOverviewStore = useEventOverviewStore()
  // The store name and property are ours, not the CMS's, so the split always yields both halves.
  const [, storeProperty = ''] = storeStateName.split('/')
  // The property name only exists as a runtime string, so the lookup cannot be checked against the
  // store's own keys; read it through a widened view of the store instead.
  const storeState = eventOverviewStore as unknown as Record<string, CmsData[]>
  onMounted(() => viewItemList(storeState[storeProperty] || [], 0))
  if (import.meta.client) {
    watch(
      () => storeState[storeProperty],
      (events) => viewItemList(events || [], 0),
    )
  }
}

/**
 * An event that shows the rendered item on the page, for CGBR this is usually an event detail page
 */
export function setupGtmViewItem(page: CmsData) {
  if (import.meta.client) {
    const gtm = useGtm()
    setTimeout(() => {
      gtm?.push({
        event: 'view_item',
        ecommerce: {
          items: [
            {
              index: 0,
              item_brand: page.production?.organizer?.title || 'Unknown',
              item_category: page.production?.title || 'Unknown',
              item_category_2: page.room?.title || 'Unknown',
              item_date: page.eventDate || 'Unknown',
              item_genre: page.production?.genres?.map(({ title }: { title?: string }) => title)?.join(', ') || 'Unknown',
              item_id: page.eventIdentifier || 'Unknown',
              item_name: eventItemName(page),
              price: page.minPrice,
              quantity: 1,
            },
          ],
        },
      })
    }, 250)
  }
}

/**
 * An event that is triggered when an order button is clicked
 */
export function setupGtmClickOrderTickets(page: CmsData) {
  if (import.meta.client) {
    const gtm = useGtm()
    return () => {
      gtm?.push({
        event: 'click_order_tickets',
        ecommerce: {
          items: [
            {
              index: 0,
              item_brand: page.production?.organizer?.title || 'Unknown',
              item_category: page.production?.title || 'Unknown',
              item_category_2: page.room?.title || 'Unknown',
              item_date: page.eventDate || 'Unknown',
              item_genre: page.production?.genres?.map(({ title }: { title?: string }) => title)?.join(', ') || 'Unknown',
              item_id: page.eventIdentifier || 'Unknown',
              item_name: eventItemName(page),
              price: page.minPrice,
              quantity: 1,
            },
          ],
        },
      })
    }
  }
  return () => {}
}

/**
 * An event whenever a promotion is displayed to the user
 */
export function setupGtmPromoView() {
  if (import.meta.client) {
    const gtm = useGtm()
    return (promotions: { name: string; creative: string; position: number }[]) => {
      gtm?.push({
        event: 'promotionView',
        ecommerce: {
          promoView: {
            promotions,
          },
        },
      })
    }
  }
  return () => {}
}

/**
 * An event whenever a promotion is clicked by the user
 */
export function setupGtmPromoClick() {
  if (import.meta.client) {
    const gtm = useGtm()
    return function (promotions: CmsData[]) {
      gtm?.push({
        event: 'promotionClick',
        ecommerce: {
          promoClick: {
            promotions,
          },
        },
      })
    }
  }
  return () => {}
}

/**
 * An event whenever search filter is activated, i.e. a facet is enabled
 */
export function setupGtmFilterStart() {
  if (import.meta.client) {
    const gtm = useGtm()
    const route = useRoute()
    const path = unref(route).path
    return function () {
      gtm?.push({
        event: 'filter',
        eventCategory: 'filter_start',
        eventAction: path,
      })
    }
  }
  return () => {}
}

/**
 * An event whenever search filter is activated, i.e. a facet is enabled
 */
export function setupGtmFilterMenuClose() {
  if (import.meta.client) {
    const gtm = useGtm()
    return function () {
      gtm?.push({
        event: 'filter_menuclose',
      })
    }
  }
  return () => {}
}

let pendingFilterSelectClickLocation = 'Filter'

export function setGtmFilterSelectClickLocation(location: string) {
  pendingFilterSelectClickLocation = location === 'Suggestion' ? 'Suggestion' : 'Filter'
}

/**
 * An event whenever a search filter is selected, i.e. a list of facets is toggled open
 */
export function setupGtmFilterSelect() {
  if (import.meta.client) {
    const gtm = useGtm()
    const eventOverviewStore = useEventOverviewStore()

    watch(
      () => eventOverviewStore.activeFacets,
      (newActiveFacets, oldActiveFacets) => {
        // Compute the difference to ensure that we only trigger a GTM event when a facet was added
        const addedFacets = differenceWith(newActiveFacets, oldActiveFacets, isEqual)
        if (addedFacets.length === 0) {
          // Reset the flag even on early return to prevent it from leaking into the next manual filter click.
          pendingFilterSelectClickLocation = 'Filter'
          return
        }
        const clickLocation = pendingFilterSelectClickLocation
        pendingFilterSelectClickLocation = 'Filter'

        setTimeout(() => {
          for (const { facet, value } of addedFacets) {
            gtm?.push({
              event: 'filter',
              eventCategory: 'filter_select',
              eventAction: facet,
              eventLabel: String(value).toLowerCase(),
              click_location: clickLocation,
            })
          }
        })
      },
    )
  }
}

/**
 * An event whenever search filter is removed, i.e. a facet is disabled
 */
export function setupGtmFilterRemove() {
  if (import.meta.client) {
    const gtm = useGtm()
    const eventOverviewStore = useEventOverviewStore()

    watch(
      () => eventOverviewStore.activeFacets,
      (newActiveFacets, oldActiveFacets) => {
        setTimeout(() => {
          // Compute the difference to ensure that we only trigger a GTM event when a facet was removed
          for (const { facet, value } of differenceWith(oldActiveFacets, newActiveFacets, isEqual)) {
            gtm?.push({
              event: 'filter_remove',
              filter_name: value,
              filter_category: facet,
              click_location: 'Text',
            })
          }
        })
      },
    )
  }
  return () => {}
}

/**
 * An event whenever date filter is activated, i.e. a facet is enabled
 */

export function setupGtmDateFilterStart() {
  if (import.meta.client) {
    const gtm = useGtm()
    return function () {
      gtm?.push({
        event: 'date_filter_start',
      })
    }
  }
  return () => {}
}

/**
 * An event whenever date filter is set, i.e. date filter selected
 */

export function setupGtmDateFilter() {
  if (import.meta.client) {
    const gtm = useGtm()
    return function (date: string) {
      gtm?.push({
        event: 'date_filter',
        selected_date: date,
      })
    }
  }
  return () => {}
}

/**
 * An event whenever date filter is removed, i.e. tied to setupGtmFilterRemoved
 */

export function setupGtmDateFilterRemove() {
  if (import.meta.client) {
    const gtm = useGtm()
    return function (date: string) {
      gtm?.push({
        event: 'filter_remove',
        filter_name: date,
        filter_category: 'Date',
        click_location: 'Text',
      })
    }
  }
  return () => {}
}

export function setupGtmRecommendationView(page: CmsData) {
  if (import.meta.client) {
    const gtm = useGtm()
    return (event: string, { added, removed, newPath, oldPath }: { added?: string; removed?: string; newPath?: string[]; oldPath?: string[] }) => {
      gtm?.push({
        event: `recommendation_view_${event}`,
        page: {
          added,
          category: page?.__typename,
          language_code: page?.language,
          newPath,
          oldPath,
          removed,
          url: page?.url,
        },
      })
    }
  }
  return () => {}
}

export function setupGtmRecommendationInteraction() {
  // update datalayer with current question & answer, next question and a list of all given answers so far
  if (import.meta.client) {
    const gtm = useGtm()
    return (question: string, answer: string, nextQuestion: string, answeredQuestionsAmount: number) => {
      gtm?.push({
        event: 'interaction_keuzehulp',
        keuzehulp_question: question,
        keuzehulp_answer: answer,
        keuzehulp_next_question: nextQuestion,
        keuzehulp_answered_questions: answeredQuestionsAmount,
      })
    }
  }
  return () => {}
}

export function setupGtmRecommendationAudioStart() {
  if (import.meta.client) {
    const gtm = useGtm()
    return (audioQuestion: string) => {
      gtm?.push({
        event: 'start_audio_keuzehulp',
        audio_question: audioQuestion, // bijv 'piano', 'intiem', 'groots'
      })
    }
  }
  return () => {}
}

export function setupGtmRecommendationAudioInit() {
  if (import.meta.client) {
    const gtm = useGtm()
    return (audioInstance: string) => {
      gtm?.push({
        event: 'init_audio_keuzehulp',
        audio_instance: audioInstance,
      })
    }
  }
  return () => {}
}

export function setupGtmRecommendationStart() {
  const gtm = useGtm()
  return () => {
    if (import.meta.client) {
      gtm?.push({
        event: 'start_keuzehulp',
      })
    }
  }
}

/** The keuzehulp result payload; every part is optional because the caller builds it up piecemeal. */
type RecommendationEndPayload = {
  keuzehulpAnswers?: CmsData[]
  keuzehulpScores?: CmsData
  recommendedConcerts?: CmsData[]
  page?: CmsData
}

export function setupGtmRecommendationEnd(defaultPage: CmsData = {}) {
  const gtm = useGtm()
  return ({ keuzehulpAnswers = [], keuzehulpScores = {}, recommendedConcerts = [], page = {} }: RecommendationEndPayload = {}) => {
    if (import.meta.client) {
      gtm?.push({
        event: 'end_keuzehulp',
        keuzehulp_answers: keuzehulpAnswers,
        keuzehulp_scores: keuzehulpScores,
        recommended_concerts: recommendedConcerts,
      })
      gtm?.push({
        event: 'page_view',
        page: {
          category: page?.__typename || defaultPage?.__typename,
          language_code: page?.language || defaultPage?.language,
          referrer: window.__internalReferrer || document.referrer || null,
          url: page?.url || defaultPage?.url,
        },
      })
    }
  }
}

export function setupGtmCdpEvent(eventAction: string) {
  if (import.meta.client) {
    const gtm = useGtm()
    setTimeout(() => {
      gtm?.push({
        event: 'CRO_Tests',
        eventAction,
      })
    }, 250)
  }
}

export function setupGtmCdpAction() {
  const gtm = useGtm()
  return (eventAction: string) => {
    if (import.meta.client) {
      gtm?.push({
        event: 'CRO_Tests',
        eventAction,
      })
    }
  }
}

export function setupGtmCdpConcertStatus(concertState: Record<string, boolean>, hasLastTicketsStatus: boolean) {
  const concertStatusKeys = ['EXPIRED', 'SOLD_OUT', 'MOVED', 'CANCELED']

  if (Object.values(concertState).some((status) => status)) {
    for (const statusKey of concertStatusKeys) {
      if (concertState[statusKey]) {
        setupGtmCdpEvent(`CV07 - Concertstatus ${statusKey.toLowerCase()}`)
        break
      }
    }
  } else {
    setupGtmCdpEvent('CV07 - Concertstatus available')

    if (hasLastTicketsStatus) {
      setupGtmCdpEvent('CV07 - Concertstatus last tickets')
    }
  }
}

export function useSetupGtmABTestEvent() {
  const gtm = useGtm()
  return (eventAction: string) => {
    if (import.meta.client) {
      setTimeout(() => {
        gtm?.push({
          event: 'CRO_Tests',
          eventAction,
        })
      }, 250)
    }
  }
}

/**
 * An event fired when a search has been performed and results have settled.
 * V1: fires on submit (Enter / magnifying glass) or suggestion select.
 * Control: fires after a ~750 ms typing pause.
 */
export function setupGtmSearchPerformed() {
  if (import.meta.client) {
    const gtm = useGtm()
    return (resultCount: number) =>
      gtm?.push({
        event: 'search_performed',
        result_count: resultCount,
      })
  }
  return () => {}
}

/**
 * Queue search_performed until the matching search response has updated the UI state.
 */
export function setupGtmSettledSearchPerformed(completedSearchSignature: MaybeRef<string>, resultCount: MaybeRef<number>) {
  if (!import.meta.client) {
    return {
      cancelPendingSearchPerformed: () => {},
      queueSearchPerformed: () => {},
    }
  }

  const registerGtmSearchPerformed = setupGtmSearchPerformed()
  // Normalized to a ref so the watcher below works whether the caller passed a ref or a plain value.
  const completedSignature = toRef(completedSearchSignature)
  const pendingSearchSignature = ref<string | null>(null)

  const flushPendingSearchPerformed = () => {
    const expectedSearchSignature = pendingSearchSignature.value

    if (!expectedSearchSignature || unref(completedSignature) !== expectedSearchSignature) {
      return false
    }

    pendingSearchSignature.value = null
    registerGtmSearchPerformed(unref(resultCount))
    return true
  }

  watch(completedSignature, () => {
    flushPendingSearchPerformed()
  })

  return {
    cancelPendingSearchPerformed: () => {
      pendingSearchSignature.value = null
    },
    queueSearchPerformed: (expectedSearchSignature: string) => {
      pendingSearchSignature.value = expectedSearchSignature
      flushPendingSearchPerformed()
    },
  }
}

/**
 * An event fired when the user clicks on a suggestion item in the suggestion dropdown.
 */
export function setupGtmSuggestionSelect() {
  if (import.meta.client) {
    const gtm = useGtm()
    return ({ suggestionType, matchRank }: { suggestionType: string; matchRank: number }) =>
      gtm?.push({
        event: 'suggestion_select',
        suggestion_type: suggestionType,
        match_rank: matchRank,
      })
  }
  return () => {}
}

/**
 * An event fired when the search overlay/focus state opens.
 */
export function setupGtmSearchFocusOpen() {
  if (import.meta.client) {
    const gtm = useGtm()
    return () =>
      gtm?.push({
        event: 'search_focus_open',
      })
  }
  return () => {}
}

/**
 * An event fired when the search overlay/focus state closes.
 * closeMethod is optional: 'escape' | 'click_outside' | 'cross_icon'
 */
export function setupGtmSearchFocusClose() {
  if (import.meta.client) {
    const gtm = useGtm()
    return (closeMethod?: string) => {
      const payload: Record<string, unknown> = { event: 'search_focus_close' }
      if (closeMethod) {
        payload.close_method = closeMethod
      }
      gtm?.push(payload)
    }
  }
  return () => {}
}

/**
 * An event fired when the user views all results (Enter key or magnifying glass in V1).
 */
export function setupGtmSearchViewAllResults() {
  if (import.meta.client) {
    const gtm = useGtm()
    return () =>
      gtm?.push({
        event: 'search_view_all_results',
      })
  }
  return () => {}
}

/**
 * An event fired when the user clicks the red ← Home button on the agenda/overview page.
 */
export function setupGtmHomeButtonClick() {
  if (import.meta.client) {
    const gtm = useGtm()
    return () =>
      gtm?.push({
        event: 'home_button_click',
      })
  }
  return () => {}
}
